<template>
  <div>
    <div v-if="isSuccess">
      <div v-if="hasData">
        <ValuesBox
          :total="total"
          :categoriesVisible="categoriesVisible"
          :featuredCategoryVisible="featuredCategoryVisible"
          :rideCategoryGroup="rideCategoryGroup"
          :rideCategoryOrdened="rideCategoryOrdened"
          @toggleVisibility="handleToggleCategoriesVisibility"
        />

        <ChartBox :data="chartData" />
      </div>

      <EmptyState
        v-else
        :categoriesVisible="categoriesVisible"
        :minHeight="minHeight"
      />
    </div>

    <ErrorState
      v-if="isFailed"
      :categoriesVisible="categoriesVisible"
      :minHeight="minHeight"
      @loadData="loadData"
    />

    <LoadingState
      v-if="isLoading"
      :categoriesVisible="categoriesVisible"
      :minHeight="minHeight"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { isValidObject, isValidArray, objectToArray, catchError } from 'corp-front-vue-commons/src/utils/functions'
import requestStatusMixin from 'mixins/requestStatus'
import ValuesBox from './valuesBox'
import ChartBox from './chartBox'
import EmptyState from './EmptyState'
import ErrorState from './ErrorState'
import LoadingState from './LoadingState'

export default {
  name: 'Performance',
  mixins: [requestStatusMixin],
  props: {
    filters: {
      type: Object,
      default: () => {}
    }
  },
  components: {
    ValuesBox,
    ChartBox,
    EmptyState,
    ErrorState,
    LoadingState

  },
  computed: {
    ...mapState({
      company: ({ company }) => company.selectedCompany
    }),

    hasData () {
      return isValidObject(this.total) &&
        (
          isValidObject(this.rideCategoryGroup) &&
          this.rideCategoryGroup.mostUsed &&
          this.rideCategoryGroup.mostUsed.percentage > 0
        ) &&
        isValidArray(this.rideCategoryOrdened) &&
        isValidObject(this.chartData)
    },

    rideCategoryOrdened () {
      return objectToArray(this.$t('Common.ridesCategoriesList'))
        .map(cat => {
          const name = cat.value
          const categoriesPercent = isValidArray(this.categoriesPercent)
            ? this.categoriesPercent
            : []
          const categoryTarget = categoriesPercent.find(c => c.category === name)
          const percentage = categoryTarget ? categoryTarget.categoryPercentile : 0
          return { ...cat, percentage }
        })
        .sort((a, b) => a.percentage < b.percentage ? 1 : -1)
    },

    rideCategoryGroup () {
      const [head = [], ...tail] = this.rideCategoryOrdened || []
      const mostUsed = this.isValidObject(head) ? { ...head } : {}
      const lessUsedSum = tail.reduce((acc, item) => {
        acc += item.percentage
        return acc
      }, 0)
      return { mostUsed, lessUsedSum }
    },

    featuredCategoryVisible () {
      return !this.categoriesVisible &&
        this.isValidObject(this.rideCategoryGroup.mostUsed) &&
        this.rideCategoryGroup.mostUsed.percentage > 0
    }
  },
  data () {
    return {
      namespace: 'Components.Performance',
      minHeight: 384,
      categoriesVisible: false,
      total: {},
      categoriesPercent: [],
      chartData: {},
      preventReload: false
    }
  },
  methods: {
    isValidObject (val) {
      return isValidObject(val)
    },

    isValidArray (val) {
      return isValidArray(val)
    },

    handleToggleCategoriesVisibility () {
      this.categoriesVisible = !this.categoriesVisible
    },

    async loadData () {
      if (this.preventReload) return

      this.preventReload = true
      this.setStatus(STATUS_IS_LOADING)
      this.categoriesVisible = false

      try {
        const { startDate = null, endDate = null } = this.filters || {}
        const companyId = this.company ? this.company.id : null
        const { data: res } = await this.$apiService.reports.getChart(
          { startDate },
          { endDate },
          { companyId }
        )

        if (
          (this.isValidObject(res.data) && res.data.total) &&
          (this.isValidObject(res.data) && res.data.categories) &&
          (this.isValidObject(res.data) && this.isValidArray(res.data.series))
        ) {
          // values
          this.total = { ...res.data.total }

          // categories
          this.categoriesPercent = [...res.data.categories]

          // chart data
          if (this.isValidObject(res.data) && this.isValidArray(res.data.series)) {
            const { series } = res.data
            this.chartData.amount = series.map(item => item.amount)
            this.chartData.averageFare = series.map(item => item.averageFare)
            this.chartData.date = series.map(item => item.date)
            this.chartData.totalFare = series.map(item => item.totalFare)
          }
        }

        this.setStatus(STATUS_SUCCESS)
      } catch (error) {
        this.setStatus(STATUS_FAILED)
        catchError(error)
      }

      setTimeout(() => { this.preventReload = false }, 1000)
    }
  },
  watch: {
    filters: {
      deep: true,
      handler () {
        this.loadData()
      }
    },
    company () {
      this.loadData()
    }
  },
  mounted () {
    this.loadData()
  }
}
</script>
